import { supabase } from './supabaseClient';
import { invokeEdgeFunction } from './edgeInvoke';

export const CORE_BASE_CACHE_ID = 'current';
export const CORE_BASE_PRODUCT_CODE = 'SOFERA';

const ENTITY_KEYS = [
  'products',
  'companies',
  'people',
  'company_users',
  'company_areas',
  'company_positions',
  'person_products',
  'company_products',
  'global_roles',
  'person_global_roles',
  'invitations',
  'person_auth_links',
];

const toArray = (value) => (Array.isArray(value) ? value : []);

function indexBy(rows, key) {
  const map = new Map();
  for (const row of rows) {
    const id = row?.[key];
    if (id === null || id === undefined) continue;
    map.set(String(id), row);
  }
  return map;
}

function groupBy(rows, key) {
  const map = new Map();
  for (const row of rows) {
    const id = row?.[key];
    if (id === null || id === undefined) continue;
    const groupKey = String(id);
    if (!map.has(groupKey)) map.set(groupKey, []);
    map.get(groupKey).push(row);
  }
  return map;
}

/**
 * Normaliza o dataset devolvido pelo Core (ou lido do cache local) em coleções
 * e índices de consulta usados pela aba Base Core.
 */
export function buildCoreBaseIndex(dataset) {
  const source = dataset && typeof dataset === 'object' ? dataset : {};
  const collections = {};
  for (const key of ENTITY_KEYS) collections[key] = toArray(source[key]);

  return {
    ...collections,
    companyById: indexBy(collections.companies, 'id'),
    personById: indexBy(collections.people, 'id'),
    areaById: indexBy(collections.company_areas, 'id'),
    positionById: indexBy(collections.company_positions, 'id'),
    productById: indexBy(collections.products, 'id'),
    globalRoleById: indexBy(collections.global_roles, 'id'),
    areasByCompany: groupBy(collections.company_areas, 'company_id'),
    positionsByCompany: groupBy(collections.company_positions, 'company_id'),
  };
}

/** Escopo (pessoas e empresas) vinculado a um produto da Base Única. */
export function resolveCoreBaseProductScope(dataset, productCode = CORE_BASE_PRODUCT_CODE) {
  const index = buildCoreBaseIndex(dataset);
  const product = index.products.find(
    (item) => String(item?.code || '').trim().toUpperCase() === String(productCode || '').trim().toUpperCase(),
  ) || null;
  const productId = product ? String(product.id) : null;

  const personIds = new Set();
  const companyIds = new Set();

  if (productId) {
    for (const row of index.person_products) {
      if (String(row?.product_id) === productId && String(row?.status || '') === 'active') {
        personIds.add(String(row.person_id));
      }
    }
    for (const row of index.company_products) {
      if (String(row?.product_id) === productId && String(row?.status || '') === 'active') {
        companyIds.add(String(row.company_id));
      }
    }
  }

  return { product, productId, personIds, companyIds, index };
}

/** Lê o último snapshot da Base Única gravado localmente (RLS: Admin). */
export async function fetchCoreBaseCache() {
  const { data, error } = await supabase
    .from('core_base_cache')
    .select('product_code, generated_at, synced_at, sync_source, counts, dataset')
    .eq('id', CORE_BASE_CACHE_ID)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

/** Dispara uma nova leitura da Base Única no Core e atualiza o cache local. */
export async function syncCoreBaseSnapshot() {
  return invokeEdgeFunction('core-base', { body: { action: 'snapshot' } });
}

/** Metadados do cache + últimos eventos recebidos via webhook do Core. */
export async function fetchCoreBaseStatus() {
  return invokeEdgeFunction('core-base', { body: { action: 'status' } });
}

export function coreBaseStatusLabel(status) {
  const value = String(status || '').trim().toLowerCase();
  if (!value) return '—';
  if (value === 'active' || value === 'ativo') return 'Ativo';
  if (value === 'inactive' || value === 'inativo') return 'Inativo';
  if (value === 'suspended' || value === 'suspenso') return 'Suspenso';
  if (value === 'pending' || value === 'pendente') return 'Pendente';
  return String(status);
}

const normalizeSourceProduct = (value) => String(value || '').trim().toUpperCase();

/**
 * Indica se a pessoa já tem identidade ativa no Acesso Único (convite aceito).
 */
export function isAuthLinkActive(link) {
  if (!link) return false;
  if (String(link.status || '').toLowerCase() !== 'active') return false;
  return !link.unlinked_at;
}

/**
 * Calcula o status de acesso de uma pessoa da Base Core.
 *
 * Regra: só é "Ativo" quem já aceitou o convite e existe no AUTH CENTRAL
 * (person_auth_links com status active e sem unlinked_at). Todos os outros
 * status são derivados do status do convite, nunca do perfil local da SOFERA.
 *
 * Retorna { label, source, canResend, inviteEmail, inviteId, action }
 */
export function resolveCorePersonStatus({
  coreStatus,
  coreInvitations = [],
  authLinks = [],
  soferaPendingInvite = null,
}) {
  const core = String(coreStatus || '').trim().toLowerCase();

  // 1. Inativo/Suspenso no Core prevalece sobre tudo.
  if (core === 'inactive' || core === 'inativo') {
    return { label: 'Inativo', source: 'Core', canResend: false };
  }
  if (core === 'suspended' || core === 'suspenso') {
    return { label: 'Suspenso', source: 'Core', canResend: false };
  }

  // 2. Única condição de "Ativo": vínculo ativo no AUTH CENTRAL.
  if (authLinks.some((link) => isAuthLinkActive(link)
    && String(link?.auth_project || '').toLowerCase() === 'auth_central')) {
    return { label: 'Ativo', source: 'AUTH CENTRAL', canResend: false };
  }

  // 3. Demais status vêm do convite mais recente.
  const latestInvite = [...coreInvitations]
    .sort((a, b) => String(b?.created_at || '').localeCompare(String(a?.created_at || '')))[0];
  const inviteStatus = String(latestInvite?.status || '').trim().toLowerCase();
  const inviteSource = normalizeSourceProduct(latestInvite?.source_product) || 'Outro sistema';

  if (inviteStatus === 'pending') {
    return {
      label: 'Pendente',
      source: inviteSource,
      canResend: inviteSource === 'SOFERA',
      inviteEmail: latestInvite?.email,
    };
  }
  if (inviteStatus === 'accepted') {
    return {
      label: 'Aceito',
      source: inviteSource,
      canResend: false,
      tooltip: 'Convite aceito, mas ainda sem vínculo ativo no AUTH CENTRAL.',
    };
  }
  if (inviteStatus === 'expired') {
    return { label: 'Expirado', source: inviteSource, canResend: true, inviteEmail: latestInvite?.email };
  }
  if (inviteStatus === 'canceled' || inviteStatus === 'cancelled') {
    return { label: 'Cancelado', source: inviteSource, canResend: true, inviteEmail: latestInvite?.email };
  }
  if (inviteStatus === 'rejected') {
    return { label: 'Recusado', source: inviteSource, canResend: true, inviteEmail: latestInvite?.email };
  }

  // 4. Convite pendente local da SOFERA (reenvio permitido).
  if (soferaPendingInvite) {
    return {
      label: 'Pendente',
      source: 'SOFERA',
      canResend: true,
      inviteEmail: soferaPendingInvite.email,
      inviteId: soferaPendingInvite.id,
    };
  }

  // 5. Sem convite e sem vínculo no AUTH CENTRAL (pode convidar).
  return { label: 'Sem convite', source: '', canResend: true, action: 'create' };
}

/**
 * Monta a lista de pessoas da Base Única que ainda não existem localmente,
 * no formato aceito pelos fluxos de matrícula/convite (mesmo shape dos
 * `profiles` locais, com `fromCore` e `central_person_id`).
 */
export function buildCorePeopleCandidates({
  corePeople = [],
  coreCompanies = [],
  coreCompanyUsers = [],
  coreInvitations = [],
  corePersonAuthLinks = [],
  localUsers = [],
} = {}) {
  const localCentralIds = new Set(
    localUsers.map((u) => String(u?.central_person_id || '')).filter(Boolean),
  );
  // A maioria dos profiles locais ainda não tem central_person_id preenchido.
  // Sem esse campo o vínculo cai no e-mail: sem essa checagem, a mesma pessoa
  // entra como opção local E como opção Core (duplicação de nomes).
  const localEmails = new Set(
    localUsers.map((u) => String(u?.email || '').trim().toLowerCase()).filter(Boolean),
  );

  const companyUserByPerson = new Map();
  coreCompanyUsers.forEach((cu) => {
    const uid = String(cu?.user_id || '');
    if (uid && !companyUserByPerson.has(uid)) companyUserByPerson.set(uid, cu);
  });

  const coreCompanyById = new Map();
  coreCompanies.forEach((c) => { if (c?.id) coreCompanyById.set(String(c.id), c); });

  // Convites e vínculos do Acesso Único: mesma fonte da coluna STATUS da Base Core.
  const personIdByEmail = new Map();
  corePeople.forEach((p) => {
    const email = String(p?.email || '').trim().toLowerCase();
    if (email) personIdByEmail.set(email, String(p.id));
  });

  const authLinksByPerson = new Map();
  corePersonAuthLinks.forEach((link) => {
    const pid = String(link?.person_id || '');
    if (!pid) return;
    if (!authLinksByPerson.has(pid)) authLinksByPerson.set(pid, []);
    authLinksByPerson.get(pid).push(link);
  });

  const invitationsByPerson = new Map();
  coreInvitations.forEach((inv) => {
    const email = String(inv?.email || '').trim().toLowerCase();
    const pid = (inv?.person_id && String(inv.person_id)) || (email && personIdByEmail.get(email)) || null;
    if (!pid) return;
    if (!invitationsByPerson.has(pid)) invitationsByPerson.set(pid, []);
    invitationsByPerson.get(pid).push(inv);
  });

  return corePeople
    .filter((p) => {
      const pid = String(p?.id || '');
      if (!pid || localCentralIds.has(pid)) return false;
      const email = String(p?.email || '').trim().toLowerCase();
      return !(email && localEmails.has(email));
    })
    .map((p) => {
      const pid = String(p.id);
      const cu = companyUserByPerson.get(pid);
      const coreCompany = cu ? coreCompanyById.get(String(cu.company_id)) : null;
      const access = resolveCorePersonStatus({
        coreStatus: p.status,
        coreInvitations: invitationsByPerson.get(pid) || [],
        authLinks: authLinksByPerson.get(pid) || [],
      });
      return {
        id: p.id,
        email: p.email || '',
        full_name: p.full_name || p.email || 'Usuário Core',
        role: 'Aluno',
        // Ativo só com vínculo no AUTH CENTRAL; Pendente cobre convite vivo/sem convite;
        // Inativo e Suspenso ficam fora dos seletores.
        status: access.label === 'Ativo'
          ? 'Ativo'
          : (access.label === 'Inativo' || access.label === 'Suspenso' ? 'Inativo' : 'Pendente'),
        cpf: p.cpf || '',
        phone: p.phone || '',
        area: p.area || '',
        company_name: coreCompany ? (coreCompany.legal_name || coreCompany.trade_name || '') : '',
        partner_name: '',
        central_person_id: p.id,
        fromCore: true,
      };
    });
}

export function formatCoreCnpj(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 14);
  if (digits.length !== 14) return String(value || '') || '—';
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export function formatCoreCpf(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length !== 11) return String(value || '') || '—';
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function formatCorePhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return String(value || '') || '—';
}
